// src/utils/invoiceCalc.js
// Draft invoice calculator — reads from DB, applies pricing rules, returns complete payload.
// Zero DB writes. supabase passed as param — never imported directly.

import { CUSTOMS_CONFIG_TIPOS, PACKAGE_TAX_FORMULA, fetchCustomsConfigRow, missingInvoiceTaxKeys } from './customsCalc';

// ── Helpers ───────────────────────────────────────────────────────────────────

const round2 = (n) => Math.round(n * 100) / 100;

async function sha256hex(data) {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(data));
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

async function lineHash(totalWeightLbs, declaredValue, lineType, quantity, unitPrice) {
  return sha256hex(
    JSON.stringify({ weight_lb: totalWeightLbs, declared_value: declaredValue, line_type: lineType, quantity, unit_price: unitPrice })
  );
}

// ── Required rule keys — throw if any are absent ──────────────────────────────
const REQUIRED_RULES = [
  'casillero_per_lb',
  'encomienda_per_lb_low',
  'encomienda_per_lb_high',
  'encomienda_tier_threshold_lb',
  'documento_flat',
  'celular_flat',
  'surcharge_under_5lb',
  'surcharge_under_10lb',
];

// ── fetchPricingRules ─────────────────────────────────────────────────────────
// Returns { [rule_key]: number } for all currently-active rules.
// Throws if any required key is missing.
export async function fetchPricingRules(tenantId, supabase) {
  const { data, error } = await supabase
    .from('pricing_rules')
    .select('rule_key, value')
    .eq('tenant_id', tenantId)
    .is('effective_until', null);

  if (error) throw new Error(`fetchPricingRules: ${error.message}`);

  const map = {};
  for (const row of (data || [])) {
    map[row.rule_key] = parseFloat(row.value);
  }

  const missing = REQUIRED_RULES.filter((k) => !(k in map));
  if (missing.length > 0) {
    throw new Error(`Missing required pricing rules: ${missing.join(', ')}`);
  }

  return map;
}

// ── fetchInvoiceRawPayload ────────────────────────────────────────────────────
// Phase 1: all async DB reads. No calculation, no line items, no hashes.
// Returns raw data bundle + warnings raised during fetch.
export async function fetchInvoiceRawPayload({ manifestId, boxId, customerId, tenantId, supabase }) {
  const fetchWarnings = [];

  // A. Weight
  const { data: bcg, error: bcgErr } = await supabase
    .from('box_client_groups')
    .select('weight_lb')
    .eq('box_id', boxId)
    .eq('client_id', customerId)
    .eq('tenant_id', tenantId)
    .maybeSingle();

  if (bcgErr || !bcg) {
    throw new Error(`no_box_client_group_found: box_id=${boxId}, client_id=${customerId}`);
  }

  const totalWeightLbs = bcg.weight_lb;

  // B. Packages
  const { data: pkgsRaw, error: pkgsErr } = await supabase
    .from('box_packages')
    .select('package_ref_id, package_type')
    .eq('box_id', boxId)
    .eq('client_id', customerId)
    .eq('tenant_id', tenantId);

  if (pkgsErr) throw new Error(`Failed to read box_packages: ${pkgsErr.message}`);

  const pkgs = pkgsRaw || [];

  const hasOrigCasillero  = pkgs.some((p) => p.package_type === 'casillero');
  const hasOrigEncomienda = pkgs.some((p) => p.package_type === 'encomienda');
  if (hasOrigCasillero && hasOrigEncomienda) {
    const tipos = [...new Set(pkgs.map((p) => p.package_type))].join(' y ');
    throw new Error(`Esta caja mezcla paquetes de ${tipos} — se necesita revisión manual.`);
  }

  // C. Encomienda value as entered (catalog or scanned receipt) in
  // encomienda_packages.total_fob. It feeds the encomienda gate only — the tax
  // itself comes from the declaration (E). box_packages.package_ref_id →
  // encomienda_packages.id.
  const encomiendaRefIds = pkgs
    .filter((p) => p.package_type === 'encomienda')
    .map((p) => p.package_ref_id);

  let summedFob = 0;
  const encomiendaFobs = {}; // { package_ref_id: total_fob } — only refs that resolved
  if (encomiendaRefIds.length > 0) {
    const { data: encPkgs, error: encErr } = await supabase
      .from('encomienda_packages')
      .select('id, total_fob')
      .in('id', encomiendaRefIds)
      .eq('tenant_id', tenantId);

    if (encErr) throw new Error(`Failed to read encomienda_packages: ${encErr.message}`);
    for (const ep of (encPkgs || [])) {
      const fob = Number(ep.total_fob ?? 0) || 0;
      encomiendaFobs[String(ep.id)] = fob;
      summedFob += fob;
    }
  }

  // D. Pricing rules
  const rules = await fetchPricingRules(tenantId, supabase);

  // E. Declaration drafts — pre-filter to packages in this box. The declaration
  // taxes every package once, per package (calcPackageTax), and the invoice
  // reads those figures instead of recomputing them. Casillero entries are
  // keyed ship-<ref>, encomienda entries enc-<ref>.
  const { data: ddRow } = await supabase
    .from('declaration_drafts')
    .select('payload, status')
    .eq('manifest_id', manifestId)
    .maybeSingle();

  const declarationStatus = ddRow?.status ?? 'missing';

  if (pkgs.length > 0) {
    if (!ddRow) {
      fetchWarnings.push('declaration_drafts_missing');
    } else if (ddRow.status !== 'confirmed') {
      fetchWarnings.push('declaration_drafts_not_confirmed');
    }
  }

  const payloadPackages = ddRow?.payload?.packages || [];
  const declarationPackages = [];

  for (const bp of pkgs) {
    const prefix = bp.package_type === 'casillero' ? 'ship' : bp.package_type === 'encomienda' ? 'enc' : null;
    if (!prefix) continue;

    const expectedDeckKey = `${prefix}-${bp.package_ref_id}`;

    const match = payloadPackages.find((p) => p.deck_key === expectedDeckKey);

    if (!match) {
      if (!fetchWarnings.includes('package_not_in_declaration')) {
        fetchWarnings.push('package_not_in_declaration');
      }
      continue;
    }

    const taxes = match.taxes || {};

    if (bp.package_type === 'casillero' && (parseFloat(taxes.fob) || 0) === 0) {
      if (!fetchWarnings.includes('casillero_fob_missing')) {
        fetchWarnings.push('casillero_fob_missing');
      }
    }

    declarationPackages.push({
      package_ref_id: bp.package_ref_id,
      package_type:   bp.package_type,
      deck_key:       expectedDeckKey,
      taxes,
    });
  }

  // F. Customs config — one row per tipo in the box (customs_config.applies_to),
  // as stored, no hardcoded fallbacks and no borrowing the other tipo's row. A
  // missing row or rate blocks issuing instead of taxing at the wrong threshold.
  const customsConfigs = {};
  for (const tipo of CUSTOMS_CONFIG_TIPOS) {
    if (!pkgs.some((p) => p.package_type === tipo)) continue;
    customsConfigs[tipo] = await fetchCustomsConfigRow(supabase, tipo);
    if (!customsConfigs[tipo]) {
      if (!fetchWarnings.includes('customs_config_missing')) fetchWarnings.push('customs_config_missing');
    } else if (missingInvoiceTaxKeys(customsConfigs[tipo]).length > 0) {
      if (!fetchWarnings.includes('customs_config_incomplete')) fetchWarnings.push('customs_config_incomplete');
    }
  }

  // G. Customer name
  const { data: customer } = await supabase
    .from('customers')
    .select('full_name')
    .eq('id', customerId)
    .eq('tenant_id', tenantId)
    .maybeSingle();

  const customerName = customer?.full_name ?? customerId;

  return {
    raw: {
      totalWeightLbs,
      pkgs,
      summedFob,
      encomiendaFobs,
      rules,
      declarationPackages,
      declarationStatus,
      customsConfigs,
      customerName,
      manifestId,
      boxId,
      customerId,
      tenantId,
    },
    fetchWarnings,
  };
}

// ── computeInvoiceDraft ───────────────────────────────────────────────────────
// Phase 2: pure calculation from raw bundle + current overrides.
// Async only for crypto.subtle hashes — zero network calls.
export async function computeInvoiceDraft({ raw, manualDeclaredValueUsd = null, manualLineFlags = [] }) {
  const {
    totalWeightLbs, pkgs, summedFob, encomiendaFobs = {}, rules,
    declarationPackages, declarationStatus, customsConfigs = {},
    customerName, manifestId, boxId, customerId, tenantId,
  } = raw;

  const warnings = [];
  const lineItems = [];
  let sortOrder = 0;

  const flagMap = new Map();
  for (const f of manualLineFlags) flagMap.set(f.package_ref_id, f.treat_as);

  const resolvedPkgs = pkgs.map((p) => ({
    ...p,
    effective_type: flagMap.get(p.package_ref_id) ?? p.package_type,
  }));

  const documentos  = resolvedPkgs.filter((p) => p.effective_type === 'documento');
  const celulares   = resolvedPkgs.filter((p) => p.effective_type === 'celular');
  const casilleros  = resolvedPkgs.filter((p) => p.effective_type === 'casillero');
  const encomiendas = resolvedPkgs.filter((p) => p.effective_type === 'encomienda');
  const dominantType = casilleros.length > 0 ? 'casillero' : encomiendas.length > 0 ? 'encomienda' : null;

  const manualVal =
    manualDeclaredValueUsd !== null && manualDeclaredValueUsd !== undefined
      ? Number(manualDeclaredValueUsd) : null;

  // Used for freight line hashes only — authoritative FOB is invoiceTotalFob below
  const declaredValue = manualVal !== null && manualVal > 0 ? manualVal : summedFob;

  // F.1 Documentos
  for (const pkg of documentos) {
    const unitPrice = rules.documento_flat;
    lineItems.push({
      line_type: 'freight_documento', description: 'Documento — sobre',
      quantity: 1, unit_price: unitPrice, line_total: round2(unitPrice),
      pricing_rule_key: 'documento_flat', source_type: 'box_package',
      source_id: pkg.package_ref_id,
      source_snapshot_hash: await lineHash(totalWeightLbs, declaredValue, 'freight_documento', 1, unitPrice),
      sort_order: sortOrder++,
    });
  }

  // F.2 Celulares
  for (const pkg of celulares) {
    const unitPrice = rules.celular_flat;
    lineItems.push({
      line_type: 'freight_celular', description: 'Celular',
      quantity: 1, unit_price: unitPrice, line_total: round2(unitPrice),
      pricing_rule_key: 'celular_flat', source_type: 'box_package',
      source_id: pkg.package_ref_id,
      source_snapshot_hash: await lineHash(totalWeightLbs, declaredValue, 'freight_celular', 1, unitPrice),
      sort_order: sortOrder++,
    });
  }

  // F.3 Casillero freight
  if (dominantType === 'casillero') {
    const qty = Number(totalWeightLbs.toFixed(1));
    const unitPrice = rules.casillero_per_lb;
    lineItems.push({
      line_type: 'freight_casillero',
      description: `Casillero — ${qty} lb × $${unitPrice.toFixed(2)}`,
      quantity: qty, unit_price: unitPrice, line_total: round2(qty * unitPrice),
      pricing_rule_key: 'casillero_per_lb', source_type: 'box_client_group',
      source_id: `${boxId}:${customerId}`,
      source_snapshot_hash: await lineHash(totalWeightLbs, declaredValue, 'freight_casillero', qty, unitPrice),
      sort_order: sortOrder++,
    });
  }

  // F.4 Encomienda freight
  if (dominantType === 'encomienda') {
    const isHighTier = totalWeightLbs >= rules.encomienda_tier_threshold_lb;
    const unitPrice  = isHighTier ? rules.encomienda_per_lb_high : rules.encomienda_per_lb_low;
    const ruleKey    = isHighTier ? 'encomienda_per_lb_high' : 'encomienda_per_lb_low';
    const qty = Number(totalWeightLbs.toFixed(1));
    lineItems.push({
      line_type: 'freight_encomienda',
      description: `Encomienda — ${qty} lb × $${unitPrice.toFixed(2)}`,
      quantity: qty, unit_price: unitPrice, line_total: round2(qty * unitPrice),
      pricing_rule_key: ruleKey, source_type: 'box_client_group',
      source_id: `${boxId}:${customerId}`,
      source_snapshot_hash: await lineHash(totalWeightLbs, declaredValue, 'freight_encomienda', qty, unitPrice),
      sort_order: sortOrder++,
    });
  }

  // F.5 Surcharge
  let surchargeTotal = 0;
  if (totalWeightLbs < 5) {
    const unitPrice = rules.surcharge_under_5lb;
    surchargeTotal = unitPrice;
    lineItems.push({
      line_type: 'surcharge_under_5lb', description: 'Recargo por peso menor a 5 lb',
      quantity: 1, unit_price: unitPrice, line_total: unitPrice,
      pricing_rule_key: 'surcharge_under_5lb', source_type: 'box_client_group',
      source_id: `${boxId}:${customerId}`,
      source_snapshot_hash: await lineHash(totalWeightLbs, declaredValue, 'surcharge_under_5lb', 1, unitPrice),
      sort_order: sortOrder++,
    });
  } else if (totalWeightLbs < 10) {
    const unitPrice = rules.surcharge_under_10lb;
    surchargeTotal = unitPrice;
    lineItems.push({
      line_type: 'surcharge_under_10lb', description: 'Recargo por peso menor a 10 lb',
      quantity: 1, unit_price: unitPrice, line_total: unitPrice,
      pricing_rule_key: 'surcharge_under_10lb', source_type: 'box_client_group',
      source_id: `${boxId}:${customerId}`,
      source_snapshot_hash: await lineHash(totalWeightLbs, declaredValue, 'surcharge_under_10lb', 1, unitPrice),
      sort_order: sortOrder++,
    });
  }

  // F.6 DAI + IVA — read, never computed. The declaration taxed each package
  // on its own FOB (calcPackageTax); the invoice sums those per-package figures
  // for the packages in this box + customer and writes one dai and one iva line.
  // A package with no declaration entry, or one taxed by the retired formula
  // (no PACKAGE_TAX_FORMULA stamp), blocks the invoice — it never taxes zero.
  // A manual declared value only changes declared_value_usd: it is one figure
  // for the whole invoice and cannot be taxed per package.
  const usingManualValue = manualVal !== null && manualVal > 0;
  const casilleroPkgs  = resolvedPkgs.filter((p) => p.package_type === 'casillero' && p.effective_type !== 'documento');
  const encomiendaPkgs = resolvedPkgs.filter((p) => p.package_type === 'encomienda' && p.effective_type !== 'documento');
  const taxedPkgs = [...casilleroPkgs, ...encomiendaPkgs];

  const declByRef = new Map(declarationPackages.map((dp) => [`${dp.package_type}:${dp.package_ref_id}`, dp]));
  const num = (v) => Number(v ?? 0) || 0;

  let casilleroFob = 0;
  let declFob = 0;
  let sumR = 0;
  let sumDai = 0;
  let sumIva = 0;
  let undeclared = false;
  let staleFormula = false;
  for (const p of taxedPkgs) {
    const dp = declByRef.get(`${p.package_type}:${p.package_ref_id}`);
    if (!dp) { undeclared = true; continue; }
    const t = dp.taxes || {};
    if (p.package_type === 'casillero') casilleroFob += num(t.fob);
    declFob += num(t.fob);
    if (t.formula !== PACKAGE_TAX_FORMULA) { staleFormula = true; continue; }
    sumR   += num(t.r);
    sumDai += num(t.dai);
    sumIva += num(t.iva);
  }
  let encomiendaFob = 0;
  for (const p of encomiendaPkgs) encomiendaFob += Number(encomiendaFobs[String(p.package_ref_id)] ?? 0) || 0;

  // The tipos carrying value; a documento-only box falls back to the tipos it
  // was received as.
  let taxTipos = CUSTOMS_CONFIG_TIPOS.filter((t) =>
    (t === 'casillero' ? casilleroPkgs : encomiendaPkgs).length > 0);
  if (taxTipos.length === 0) {
    taxTipos = CUSTOMS_CONFIG_TIPOS.filter((t) => resolvedPkgs.some((p) => p.package_type === t));
  }
  const configMissingTipos    = taxTipos.filter((t) => !customsConfigs[t]);
  const configIncompleteTipos = taxTipos.filter((t) => customsConfigs[t] && missingInvoiceTaxKeys(customsConfigs[t]).length > 0);
  const configIncomplete = configMissingTipos.length + configIncompleteTipos.length > 0;

  // Nothing is charged unless every taxed package has a current entry.
  const taxReadable = taxedPkgs.length > 0 && !undeclared && !staleFormula && !configIncomplete;
  const invoiceTotalFob = round2(usingManualValue ? manualVal : declFob);
  const invoiceTotalDai = taxReadable ? round2(sumDai) : 0;
  const invoiceTotalIva = taxReadable ? round2(sumIva) : 0;
  const tax = {
    cif:    taxReadable ? round2(sumR) : null,
    exento: taxReadable ? invoiceTotalDai === 0 : null,
  };

  // Declaration gate, per tipo. Tax only counts as assessed when every
  // casillero and encomienda package has a declaration entry stamped with the
  // current formula. A manual 'impuesto' line added later also clears it —
  // see taxGateBlocked(). Documentos need no value.
  const taxGate = [];
  if (taxedPkgs.length > 0) {
    for (const t of configMissingTipos) taxGate.push(`customs_config_missing_${t}`);
    if (configIncompleteTipos.length > 0) taxGate.push('customs_config_incomplete');
    if (undeclared || (casilleroPkgs.length > 0 && casilleroFob <= 0)) taxGate.push('declaracion_faltante');
    if (staleFormula) taxGate.push('declaracion_formula_antigua');
    if (!usingManualValue && encomiendaPkgs.length > 0) {
      const unresolved = encomiendaPkgs.some((p) => !(String(p.package_ref_id) in encomiendaFobs));
      if (unresolved) taxGate.push('encomienda_paquete_no_encontrado');
      if (encomiendaFob <= 0) taxGate.push('encomienda_sin_valor');
    }
  }

  const taxSnapshotHash = await sha256hex(
    JSON.stringify({ declaration_status: declarationStatus, declarationPackages, manualDeclaredValueUsd })
  );

  if (invoiceTotalDai > 0) {
    lineItems.push({
      line_type: 'dai', description: 'DAI — Derecho Arancelario a la Importación',
      quantity: 1, unit_price: round2(invoiceTotalDai), line_total: round2(invoiceTotalDai),
      pricing_rule_key: null, source_type: 'customs_computed', source_id: null,
      source_snapshot_hash: taxSnapshotHash, sort_order: sortOrder++,
    });
  }

  if (invoiceTotalIva > 0) {
    lineItems.push({
      line_type: 'iva', description: 'IVA — Impuesto al Valor Agregado',
      quantity: 1, unit_price: round2(invoiceTotalIva), line_total: round2(invoiceTotalIva),
      pricing_rule_key: null, source_type: 'customs_computed', source_id: null,
      source_snapshot_hash: taxSnapshotHash, sort_order: sortOrder++,
    });
  }

  // G. Totals
  const impuestoTotal = invoiceTotalDai + invoiceTotalIva;
  const subtotal = round2(
    lineItems.filter((l) => l.line_type.startsWith('freight_')).reduce((s, l) => s + l.line_total, 0)
  );
  const total = round2(subtotal + surchargeTotal + impuestoTotal);

  const rulesVersionHash = await sha256hex(
    JSON.stringify(Object.entries(rules).sort(([a], [b]) => a.localeCompare(b)))
  );

  return {
    draft_invoice: {
      tenant_id:                  tenantId,
      manifest_id:                manifestId,
      box_id:                     boxId,
      customer_id:                customerId,
      customer_name:              customerName,
      currency:                   'USD',
      total_weight_lbs:           totalWeightLbs,
      declared_value_usd:         round2(invoiceTotalFob),
      // 'encomienda_fob' needs the invoices_declared_value_source_check widened
      // first; until then any encomienda-valued invoice leaves it null.
      declared_value_source:
        usingManualValue ? 'manual'
        : invoiceTotalFob <= 0 ? 'none'
        : encomiendaFob > 0 ? null
        : 'declaration_drafts',
      subtotal,
      surcharge_total:            round2(surchargeTotal),
      impuesto_total:             round2(impuestoTotal),
      dai_total:                  round2(invoiceTotalDai),
      iva_total:                  round2(invoiceTotalIva),
      customs_fob_total:          round2(invoiceTotalFob),
      total,
      manual_declared_value_used: usingManualValue,
      declaration_status:         declarationStatus,
      customs_cif:                tax.cif ?? null,
      customs_exento:             tax.exento ?? null,
      tax_gate:                   taxGate,
    },
    line_items: lineItems,
    warnings,
    metadata: {
      calculated_at:      new Date().toISOString(),
      rules_version_hash: rulesVersionHash,
    },
  };
}

// ── Declaration gate ──────────────────────────────────────────────────────────
// True while the invoice must not be issued: the gate has reasons and no
// manual 'impuesto' pass-through line has been recorded to replace the
// missing assessment.
export function taxGateBlocked(taxGate, lineItems) {
  if (!taxGate || taxGate.length === 0) return false;
  const hasManualPasante = (lineItems || []).some((l) => l.line_type === 'impuesto' && (Number(l.line_total ?? 0) || 0) > 0);
  return !hasManualPasante;
}

// True when every gate reason is a missing value the operator may bill later
// (soft gate: issue with impuesto pendiente). Any other reason — config
// missing, stale formula, failed check — keeps the hard lock.
const SOFT_GATE_REASONS = ['declaracion_faltante', 'encomienda_sin_valor'];
export function taxGateSoftOnly(taxGate) {
  if (!taxGate || taxGate.length === 0) return false;
  return taxGate.every((g) => SOFT_GATE_REASONS.includes(g));
}

// ── calculateInvoiceDraft ─────────────────────────────────────────────────────
// Convenience wrapper — preserves the original API for Mode B Recalcular and tests.
export async function calculateInvoiceDraft(params) {
  const { raw, fetchWarnings } = await fetchInvoiceRawPayload(params);
  const result = await computeInvoiceDraft({
    raw,
    manualDeclaredValueUsd: params.manualDeclaredValueUsd,
    manualLineFlags:        params.manualLineFlags,
  });
  return {
    ...result,
    warnings: [...fetchWarnings, ...result.warnings],
  };
}
